import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} SchoolLicenseStats
 * @property {number} total_licenses - Total licenses allocated
 * @property {number} used_licenses - Licenses used
 * @property {number} available_licenses - Licenses available
 */

/**
 * @typedef {object} SchoolLicenseActivity
 * @property {string} date - Activity date
 * @property {string} action - Action performed (e.g., "Initial License Allocation")
 * @property {number} licenses_added - Number of licenses added
 * @property {string} expiry_date - License expiry date
 * @property {string} amount - Amount paid
 * @property {string} payment_type - Payment type (e.g., "Offline", "Online")
 * @property {string} status - Status (e.g., "partial", "complete")
 * @property {string} currency - Currency of the amount (e.g., "INR", "USD")
 */

/**
 * @typedef {object} SchoolLicenseOverviewData
 * @property {SchoolLicenseStats} stats - License statistics
 * @property {SchoolLicenseActivity[]} activity - License activity history
 */

/**
 * Custom hook to fetch license overview for a school
 */
export const useGetLicenseOverview = () => {
    const [licenseData, setLicenseData] = useState(
        /** @type {SchoolLicenseOverviewData | null | undefined} */(undefined)
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const { axiosInstance } = useAxios();

    /**
     * Fetch School License Overview
     * @async
     * @returns {Promise<void>}
     */
    const getLicenseOverview = async () => {
        try {
            setLoading(true);
            setError("");

            const url = `${TENANT_DOMAIN}/school/admin/licenses/overview`;
            const response = await axiosInstance.get(url);

            setLicenseData(response?.data?.data || null);
        } catch (err) {
            setLicenseData(null);

            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        licenseData,
        loading,
        error,
        getLicenseOverview,
    };
};